import { boundMethod } from 'autobind-decorator';
/**
 * This would be the _"root of all containers"_, the one that setups the app basic functionalities
 * and renders all the other containers.
 */
class AppContainer {
  /**
   * @param {ApiAuthorizationInterceptor} apiAuthorizationInterceptor To trigger the Authorization Interceptor service.
   * @param {ApiVersionInterceptor}       apiVersionInterceptor       To trigger the Version Interceptor service.
   * @param {AppAPI}                      appAPI                      To be able to set the authorization token when the
   *                                                                  session gets updated.
   * @param {AppConfiguration}            appConfiguration            To get the app title and add it to the router.
   * @param {AppRouting}                  appRouting                  To setup the routes and the authorization step on
   *                                                                  the pipeline.
   * @param {AppRoutingStep}              appRoutingStep              The authorization step that will validate the user
   *                                                                  permissions before accessing a route.
   * @param {AppSession}                  appSession                  To listen for session changes to update the UI.
   * @param {Intercom}                    intercom                    To trigger the Intercom integration.
   * @param {ViewMessages}                viewMessages                To send notifications to the user upon having
   *                                                                  version conflicts triggered by
   *                                                                  apiConflictResponses.
   * @param {AppNavigation}               NAVIGATION                  To get all the routes the app will use.
   */
  constructor(
    apiAuthorizationInterceptor,
    apiVersionInterceptor,
    appAPI,
    appConfiguration,
    appRouting,
    appRoutingStep,
    appSession,
    productFeedsJobsNotifications,
    intercom,
    viewMessages,
    NAVIGATION,
  ) {
    'inject';

    /**
     * A local reference for the `apiAuthorizationInterceptor` service.
     * @type {ApiAuthorizationInterceptor}
     * @access protected
     */
    this._apiAuthorizationInterceptor = apiAuthorizationInterceptor;
    /**
     * A local reference for the `apiVersionInterceptor` service.
     * @type {ApiVersionInterceptor}
     * @access protected
     */
    this._apiVersionInterceptor = apiVersionInterceptor;
    /**
     * A local reference for the `appAPI` service.
     * @type {AppAPI}
     * @access protected
     */
    this._appAPI = appAPI;
    /**
     * A local reference for the `appConfiguration` service.
     * @type {AppConfiguration}
     * @access protected
     */
    this._appConfiguration = appConfiguration;
    /**
     * A local reference for the `appRouting` service.
     * @type {AppRouting}
     * @access protected
     */
    this._appRouting = appRouting;
    /**
     * A local reference for the `appRoutingStep` service.
     * @type {AppRoutingStep}
     * @access protected
     */
    this._appRoutingStep = appRoutingStep;
    /**
     * A local reference for the `appSession` service.
     * @type {AppSession}
     * @access protected
     */
    this._appSession = appSession;
    this._productFeedsJobsNotifications = productFeedsJobsNotifications;
    /**
     * A local reference for the `viewMessages` service.
     * @type {ViewMessages}
     * @access protected
     */
    this._viewMessages = viewMessages;
    /**
     * A local reference for the `intercom` service.
     * @type {Intercom}
     * @access protected
     */
    this._intercom = intercom;
    /**
     * A local reference for the `NAVIGATION` constant.
     * @type {AppNavigation}
     * @access protected
     */
    this._NAVIGATION = NAVIGATION;
    this._sessionSubscriptions = [];
    /**
     * Wether or not to show the menu on the UI.
     * @type {boolean}
     */
    this.showMenu = false;
  }
  /**
   * Starts listening for changes on the user session.
   */
  activate() {
    this._sessionSubscriptions = [
      this._appSession.onSessionCreated(this._onSessionCreated),
      this._appSession.onSessionLoaded(this._onSessionLoaded),
      this._appSession.onSessionEnd(this._onSessionEnd),
      this._appAPI.addInterceptor(this._apiAuthorizationInterceptor),
      this._appAPI.addInterceptor(this._apiVersionInterceptor.interceptorWithAction(() => {
        this._viewMessages.notification('A new version has been released');
      })),
    ];
  }
  /**
   * Add the authorization step on the router pipeline and define all the app routes.
   * @param {RouterConfiguration} config The router configuration.
   */
  configureRouter(config) {
    config.title = this._appConfiguration.get('appHTMLTitle');
    config.addPipelineStep('authorize', this._appRoutingStep);
    config.options.pushState = true;
    config.map(this._appRouting.extractRoutes(this._NAVIGATION));
  }
  /**
   * Stops listening for changes on the user session.
   */
  detached() {
    this._sessionSubscriptions.forEach((subscription) => {
      subscription.dispose();
    });
  }
  @boundMethod
  _onSessionCreated(session) {
    this._onSessionLoaded(session, false);
    this._productFeedsJobsNotifications.loadNotifications(true);
    this._productFeedsJobsNotifications.showNotifications();
  }
  @boundMethod
  _onSessionEnd() {
    this._productFeedsJobsNotifications.dismiss();
    this.showMenu = false;
    this._appAPI.setAuthorizationToken();
    this._intercom.shutdown();
  }
  @boundMethod
  _onSessionLoaded(session, showNotifications = true) {
    this.showMenu = true;
    this._appAPI.setAuthorizationToken(session.token);
    this._intercom.boot(session.user);
    if (showNotifications) {
      this._productFeedsJobsNotifications.loadNotifications(true);
      this._productFeedsJobsNotifications.showNotifications();
    }
  }
}

export { AppContainer };
